import theme from '../../theme';
import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LEARN_SUBDOMAIN_URL } from '../../lib/subdomain';

const NAV_CSS = `
  .navbar-root {
    background: ${theme.bgDark};
    height: 52px;
    padding: 0 40px;
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: center;
    column-gap: 40px;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(184, 149, 90, 0.2);
  }
  .navbar-header {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
    position: relative;
    z-index: 2;
  }
  .navbar-logo {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    font-weight: 400;
    color: ${theme.textPrimary};
    text-decoration: none;
    letter-spacing: 0.02em;
    flex-shrink: 0;
    transition: color 0.2s;
  }
  .navbar-logo:hover {
    color: ${theme.gold};
  }
  .navbar-nav {
    display: flex;
    gap: 22px;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
  }
  @media (min-width: 1281px) and (max-width: 1420px) {
    .navbar-root {
      padding: 0 24px;
      column-gap: 32px;
    }
    .navbar-nav {
      gap: 14px;
    }
  }
  @media (min-width: 1201px) and (max-width: 1280px) {
    .navbar-root {
      padding: 0 12px;
      column-gap: 14px;
    }
    .navbar-nav {
      gap: 7px;
    }
  }
  .navbar-link {
    color: ${theme.textMuted};
    text-decoration: none;
    font-size: 12px;
    font-weight: 400;
    font-family: 'Josefin Sans', sans-serif;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding-bottom: 2px;
    transition: color 0.2s ease;
    white-space: nowrap;
  }
  .navbar-link:hover {
    color: ${theme.gold};
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-color: rgba(184, 149, 90, 0.4);
  }
  .navbar-link.active {
    color: ${theme.gold};
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-color: rgba(184, 149, 90, 0.4);
  }
  .nav-toggle-btn {
    display: none;
  }

  @media (max-width: 1200px) {
    .navbar-root {
      height: 54px;
      padding: 0 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .navbar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
    .navbar-logo {
      font-size: 16px;
    }
    .nav-toggle-btn {
      display: flex !important;
      position: relative;
      width: 44px;
      height: 44px;
      background: transparent;
      border: none;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      margin-right: -10px;
      padding: 0;
    }
    .hamburger-inner {
      position: relative;
      width: 20px;
      height: 12px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .hamburger-line {
      display: block;
      height: 1.5px;
      width: 100%;
      background-color: ${theme.gold};
      transition: all 0.3s ease;
    }
    /* Hamburger animation when open */
    .nav-toggle-btn.open .hamburger-inner .hamburger-line:nth-child(1) {
      transform: translateY(5px) rotate(45deg);
    }
    .nav-toggle-btn.open .hamburger-inner .hamburger-line:nth-child(2) {
      opacity: 0;
    }
    .nav-toggle-btn.open .hamburger-inner .hamburger-line:nth-child(3) {
      transform: translateY(-5.5px) rotate(-45deg);
    }

    .navbar-nav {
      position: fixed;
      top: 54px;
      left: 0;
      width: 100%;
      height: calc(100vh - 54px);
      background: rgba(13, 27, 46, 0.98);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2.2rem;
      transition: transform 0.35s cubic-bezier(0.77, 0.2, 0.05, 1.0), opacity 0.3s ease;
      transform: translateY(-100%);
      opacity: 0;
      pointer-events: none;
    }
    .navbar-nav.open {
      transform: translateY(0);
      opacity: 1;
      pointer-events: auto;
    }
    .navbar-link {
      font-size: 0.9rem !important;
      letter-spacing: 0.25em !important;
      color: #8da0b3 !important;
    }
    .navbar-link.active {
      color: ${theme.gold} !important;
      text-decoration: underline !important;
      text-underline-offset: 6px !important;
    }
  }
`;

function NavBar() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <nav className="navbar-root">
      <style>{NAV_CSS}</style>

      <div className="navbar-header">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          Austrian Process
        </Link>
        <button 
          type="button"
          className={`nav-toggle-btn ${isOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <div className="hamburger-inner">
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </div>
        </button>
      </div>

      <div className={`navbar-nav ${isOpen ? 'open' : ''}`}>
        <NavLink to="/process" className="navbar-link" onClick={closeMenu}>
          <span>Why Austrian</span>
        </NavLink>
        <NavLink to="/explainers" className="navbar-link" onClick={closeMenu}>
          <span>Explainers</span>
        </NavLink>
        <NavLink to="/garrison-triangle" className="navbar-link" onClick={closeMenu}>
          <span>The Triangle</span>
        </NavLink>
        <NavLink to="/fred" className="navbar-link" onClick={closeMenu}>
          <span>Interest Rates</span>
        </NavLink>
        <NavLink to="/calculation-problem" className="navbar-link" onClick={closeMenu}>
          <span>The Calculation Problem</span>
        </NavLink>
        <NavLink to="/ask-austrian" className="navbar-link" onClick={closeMenu}>
          <span>Ask Austrian</span>
        </NavLink>
        <NavLink to="/quiz" className="navbar-link" onClick={closeMenu}>
          <span>The Quiz</span>
        </NavLink>
        <NavLink to="/shelf" className="navbar-link" onClick={closeMenu}>
          <span>The Shelf</span>
        </NavLink>
        <NavLink to="/reading-notes" className="navbar-link" onClick={closeMenu}>
          <span>Reading Notes</span>
        </NavLink>
        <a href={LEARN_SUBDOMAIN_URL} className="navbar-link">
          <span>Learn</span>
        </a>
      </div>
    </nav>
  );
}

export default NavBar;
